/** The assurance queue, from the browser's side — **gate 27 / ASR-02**.
 *
 * Five routes existed, were tested, and nothing called them: `GET`/`POST /assurance/requests`,
 * `…/responses`, `…/resolve` and `…/withdraw`. This is the client for them.
 *
 * **Who may do what is the server's decision, mirrored here for one reason.** The screen tells a
 * role who can rather than showing a control that predictably refuses (design rule 6, `KEEP.md`
 * 17), so it has to know the roles. `api/assurance.py` is the authority; these are its three
 * tuples, and `assurance.test.tsx` mutation-proves that a role the server refuses is never shown
 * the control for it.
 *
 * **A failed read throws.** `return data ?? []` made a failed read render as *"no requests"* ten
 * times over in this codebase, and on this screen it would render as an assurance file in which
 * nobody had asked anything. `useAssuranceQueue` has no defaulting, so `Loaded` can tell loading,
 * failed and empty apart.
 *
 * **Nothing here resolves an identifier to a name.** `raised_by`, `responded_by` and `closed_by`
 * are UUIDs and stay UUIDs (rule 4's habit applied to a view an auditor reads): a name next to a
 * statement in an assurance file is the one thing in it an erasure request could later change.
 * Whether an auditor should be shown names is a product and legal question (B-02), not a
 * convenience to add here.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type AssuranceRequest = components["schemas"]["AssuranceRequestOut"];
export type AssuranceResponse = components["schemas"]["AssuranceResponseOut"];
type RaiseBody = components["schemas"]["AssuranceRequestIn"];
/** Taken from the body type rather than `schemas["RequestKind"]`: that component name collides with
 * another router's, FastAPI renames both when it happens, and a screen that named it directly would
 * stop compiling for a reason that has nothing to do with it. */
export type RequestKindName = RaiseBody["kind"];

/** Whether an answer cites a record the reader may not open.
 *
 * The server withholds the id and says so with `cites_outside_engagement`. It is read through this
 * function because the generated type predates the field: `make api-types` is run once at
 * integration, and until then a direct property access does not compile. After regeneration the
 * cast is a no-op and this can read the property directly.
 */
export function citationWithheld(response: AssuranceResponse): boolean {
  return (response as AssuranceResponse & { cites_outside_engagement?: boolean })
    .cites_outside_engagement === true;
}

export const assuranceKeys = {
  all: () => ["assurance"] as const,
  queue: () => ["assurance", "requests"] as const,
  /** Under `queue()` and `all()`, so every write's invalidation reaches it. */
  about: (recordId: string) => ["assurance", "requests", "record", recordId] as const,
};

/** The three things a provider asks for, in the order the standards distinguish them, each with the
 * sentence that says what a good answer is — a `sample` is answered with a record and the others
 * need not be, which is the one rule the form must not let a reader find out by being refused. */
export const KINDS: ReadonlyArray<{
  kind: RequestKindName;
  label: string;
  answeredWith: string;
}> = [
  {
    kind: "sample",
    label: "Sample",
    answeredWith: "Answered with a record — the document, the activity record or the filing itself.",
  },
  {
    kind: "assertion",
    label: "Assertion",
    answeredWith: "Answered with a position the organisation takes responsibility for.",
  },
  {
    kind: "question",
    label: "Question",
    answeredWith: "Answered in words; it may have no document behind it.",
  },
];

/** Mirrors `api/assurance.py`'s `MAY_READ`. */
export const MAY_READ: readonly string[] = ["admin", "approver", "contributor", "viewer", "auditor"];
/** Mirrors `MAY_RAISE`, which is also who may close. */
export const MAY_RAISE: readonly string[] = ["auditor", "admin"];
/** Mirrors `MAY_RESPOND`. An Auditor is deliberately absent: an auditor who wrote the answer would
 * be auditing evidence they produced. */
export const MAY_RESPOND: readonly string[] = ["admin", "approver", "contributor"];

/** States a request may still be acted on from. `service.LIVE`. */
const LIVE: readonly string[] = ["open", "answered"];

export function isLive(request: AssuranceRequest): boolean {
  return LIVE.includes(request.state);
}

/** Lower-case hex in the canonical 8-4-4-4-12 shape. A convenience that keeps the form from
 * sending something the server will certainly refuse, not a rule: the server resolves the id under
 * the session's own scope and engagement, and answers the same 404 for a malformed-but-valid id as
 * for one that was never used. */
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What a signed-in person may do to one request, with the sentence for each thing they may not.
 *
 * **A sentence, never a disabled control.** `answer` and `close` are each either `null` (may) or the
 * reason they may not, written once here so the screen and its tests read the same words.
 */
export interface Standing {
  /** `null` when this person may answer this request. */
  answer: string | null;
  /** `null` when this person may close it. */
  close: string | null;
}

export function standing(
  role: string,
  userId: string,
  request: AssuranceRequest,
): Standing {
  const live = isLive(request);
  const closedWords = `This request is ${request.state}; it takes no further responses. A reopened question is a new request, so the file shows both.`;

  let answer: string | null = null;
  if (!live) answer = closedWords;
  else if (role === "auditor") {
    answer =
      "The organisation answers, not the auditor. An auditor who wrote the response would be answering their own question, and the thread would prove nothing.";
  } else if (!MAY_RESPOND.includes(role)) {
    answer =
      "An Admin, an Approver or a Contributor answers. A Viewer reads the queue and changes none of it.";
  }

  let close: string | null = null;
  if (!live) close = closedWords;
  else if (!MAY_RAISE.includes(role)) {
    close =
      "Only the Auditor or Admin who raised a request closes it. Answering is how the organisation responds; whether the answer was sufficient is the reviewer's judgement.";
  } else if (request.raised_by !== userId) {
    close =
      "Only the person who raised this request closes it, and that is not you. A second reviewer on the same engagement did not ask, and does not know whether it was answered.";
  }
  return { answer, close };
}

/** Every request this person may see, each with its whole thread.
 *
 * **Not defaulted, not paginated, not summarised** — the server's reasons (`list_requests`): the
 * thread is the deliverable. For an engaged Auditor the list is the requests about records inside
 * the engagement, and the screen says so rather than letting a short list read as a short queue.
 */
export function useAssuranceQueue(enabled: boolean) {
  return useQuery({
    queryKey: assuranceKeys.queue(),
    enabled,
    // A 403 is an answer, and the screen words it; retrying it is noise.
    retry: false,
    queryFn: async (): Promise<AssuranceRequest[]> => {
      const { data, error, response } = await api.GET("/api/v1/assurance/requests", {});
      if (error) throw refusalFrom(response, error, "The assurance queue could not be read.");
      // A body that is not an array is a server that answered with something else. Returning `[]`
      // would be the same lie as a dropped request.
      if (!Array.isArray(data)) throw new Error("The assurance queue could not be read.");
      return data;
    },
  });
}

/** The assurance requests about **one record**, for a figure's receipt and its record page.
 *
 * **Three outcomes, and they are not interchangeable.** `null` is *"this reader is not shown the
 * queue"* — a 403 or a 404 (the engagement clamp answers one of them for a record outside the
 * engagement) — and the caller renders nothing, because a sentence there would confirm that a
 * queue exists. An array, possibly empty, is the queue's answer. **Anything else throws**: a 5xx or
 * a dropped connection must reach the screen as a failed read, since an empty list on this section
 * is a claim that no assurance provider has asked about the figure.
 */
export function useAssuranceAbout(recordId: string) {
  return useQuery({
    queryKey: assuranceKeys.about(recordId),
    retry: false,
    queryFn: async (): Promise<AssuranceRequest[] | null> => {
      const { data, error, response } = await api.GET("/api/v1/assurance/requests", {
        params: { query: { record_id: recordId } },
      });
      if (response.status === 403 || response.status === 404) return null;
      if (error) throw refusalFrom(response, error, "The assurance questions could not be read.");
      if (!Array.isArray(data)) throw new Error("The assurance questions could not be read.");
      return data;
    },
  });
}

export interface RaiseDraft {
  recordId: string;
  kind: RequestKindName;
  reference: string;
  question: string;
}

export function raiseBody(draft: RaiseDraft): RaiseBody {
  const reference = draft.reference.trim();
  return {
    record_id: draft.recordId.trim(),
    kind: draft.kind,
    question: draft.question.trim(),
    // Absent rather than empty: the reference is the provider's own, "never generated here".
    ...(reference ? { reference } : {}),
  };
}

/** Every write drops the whole queue, because every one of them changes a request's state and the
 * thread under it, and a stale "answered" beside a fresh response is the failure this screen
 * cannot afford. */
function useInvalidatingMutation<V>(write: (variables: V) => Promise<AssuranceRequest>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assuranceKeys.all() }),
  });
}

export function useRaiseRequest() {
  return useInvalidatingMutation(async (draft: RaiseDraft) => {
    const { data, error, response } = await api.POST("/api/v1/assurance/requests", {
      body: raiseBody(draft),
    });
    if (error) throw refusalFrom(response, error, "That request could not be raised.");
    return data;
  });
}

export function useRespond() {
  return useInvalidatingMutation(
    async (input: { requestId: string; body: string; citesRecordId: string }) => {
      const cites = input.citesRecordId.trim();
      const { data, error, response } = await api.POST(
        "/api/v1/assurance/requests/{request_id}/responses",
        {
          params: { path: { request_id: input.requestId } },
          body: { body: input.body.trim(), ...(cites ? { cites_record_id: cites } : {}) },
        },
      );
      if (error) throw refusalFrom(response, error, "That response could not be sent.");
      return data;
    },
  );
}

export function useResolve() {
  return useInvalidatingMutation(async (input: { requestId: string; resolution: string }) => {
    const { data, error, response } = await api.POST(
      "/api/v1/assurance/requests/{request_id}/resolve",
      {
        params: { path: { request_id: input.requestId } },
        body: { resolution: input.resolution.trim() },
      },
    );
    if (error) throw refusalFrom(response, error, "That request could not be resolved.");
    return data;
  });
}

export function useWithdraw() {
  return useInvalidatingMutation(async (input: { requestId: string; reason: string }) => {
    const { data, error, response } = await api.POST(
      "/api/v1/assurance/requests/{request_id}/withdraw",
      {
        params: { path: { request_id: input.requestId } },
        // The route's body is `AssuranceCloseIn`, whose field is `resolution` for both closures.
        body: { resolution: input.reason.trim() },
      },
    );
    if (error) throw refusalFrom(response, error, "That request could not be withdrawn.");
    return data;
  });
}
